import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../../components/dashboard/DashboardIcons';
import { toast } from '../../../lib/toast';
import {
  API, auth, Field, Modal, PageHead, PrimaryBtn, Paginator, errMsg, inputCls, fmtDate,
  PLAN_TYPE_LABELS, PLAN_STATUS_LABELS, PLAN_STATUS_COLORS, StatusPill, StudioEmpty,
} from './shared';

const emptyForm = () => ({ name: '', plan_type: 'weekly', start_date: '', end_date: '', description: '' });

export default function TrainingPlansPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState('mine');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => { if (params.get('new') === '1') setShowCreate(true); }, [params]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/plans`, { ...auth(), params: { scope, page, page_size: 10 } });
      setItems(res.data.items); setTotal(res.data.total);
    } catch (e) { setError(errMsg(e, 'تعذر تحميل الخطط')); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope, page]);

  const createPlan = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) { toast.error('الاسم والتواريخ مطلوبة'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/coach-studio/plans`, form, auth());
      toast.success('تم إنشاء الخطة');
      setShowCreate(false); setForm(emptyForm());
      navigate(`/coach/studio/plans/${res.data.id}`);
    } catch (e) { toast.error(errMsg(e, 'تعذر إنشاء الخطة')); } finally { setSaving(false); }
  };

  const duplicatePlan = async (id) => {
    try {
      const res = await axios.post(`${API}/coach-studio/plans/${id}/duplicate`, {}, auth());
      toast.success('تم نسخ الخطة');
      navigate(`/coach/studio/plans/${res.data.id}`);
    } catch (e) { toast.error(errMsg(e, 'تعذر نسخ الخطة')); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('هل تريد حذف هذه الخطة؟')) return;
    try { await axios.delete(`${API}/coach-studio/plans/${id}?confirm=true`, auth()); toast.success('تم حذف الخطة'); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر الحذف')); }
  };

  return (
    <div className="space-y-4" data-testid="training-plans-page">
      <PageHead title="الخطط التدريبية" desc="خطط يومية وأسبوعية وشهرية وموسمية" action={
        <PrimaryBtn onClick={() => setShowCreate(true)} data-testid="create-plan-btn"><Icon name="plus" className="w-4 h-4" /> خطة جديدة</PrimaryBtn>
      } />
      <div className="flex gap-2">
        {[{ k: 'mine', l: 'خططي' }, { k: 'shared', l: 'مشاركة معي' }].map((s) => (
          <button key={s.k} onClick={() => setScope(s.k)} data-testid={`plans-scope-${s.k}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${scope === s.k ? 'bg-[#16233b] text-white' : 'bg-slate-100 text-slate-600'}`}>{s.l}</button>
        ))}
      </div>
      {loading ? <div className="p-8 text-center text-sm text-slate-400" data-testid="plans-loading">جاري التحميل...</div> :
        error ? <div className="p-8 text-center text-sm text-red-500" data-testid="plans-error">{error}</div> :
        items.length === 0 ? <StudioEmpty text="لا توجد خطط بعد" icon={<Icon name="clipboard" className="w-6 h-6" />} /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white" data-testid="plans-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-right text-xs text-slate-500">
              <tr><th className="p-3">الاسم</th><th className="p-3">النوع</th><th className="p-3">الفترة</th><th className="p-3">الحالة</th><th className="p-3">إجراءات</th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`plan-row-${p.id}`}>
                  <td className="p-3 font-semibold text-slate-800 cursor-pointer" onClick={() => navigate(`/coach/studio/plans/${p.id}`)}>{p.name}</td>
                  <td className="p-3 text-slate-600">{PLAN_TYPE_LABELS[p.plan_type]}</td>
                  <td className="p-3 text-slate-500 text-xs">{fmtDate(p.start_date)} — {fmtDate(p.end_date)}</td>
                  <td className="p-3"><StatusPill status={p.status} labels={PLAN_STATUS_LABELS} colors={PLAN_STATUS_COLORS} /></td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => navigate(`/coach/studio/plans/${p.id}`)} className="text-xs text-teal-700" data-testid={`plan-open-btn-${p.id}`}>فتح</button>
                      {scope === 'mine' && <button onClick={() => duplicatePlan(p.id)} className="text-xs text-slate-500" data-testid={`plan-duplicate-btn-${p.id}`}>نسخ</button>}
                      {scope === 'mine' && <button onClick={() => deletePlan(p.id)} className="text-xs text-red-500" data-testid={`plan-delete-btn-${p.id}`}>حذف</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Paginator page={page} total={total} limit={10} onChange={setPage} />

      <Modal open={showCreate} title="خطة تدريبية جديدة" onClose={() => setShowCreate(false)}>
        <div className="space-y-3">
          <Field label="اسم الخطة" required><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="plan-name-input" /></Field>
          <Field label="نوع الخطة">
            <select value={form.plan_type} onChange={(e) => setForm({ ...form, plan_type: e.target.value })} className={inputCls} data-testid="plan-type-select">
              {Object.entries(PLAN_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="تاريخ البداية" required><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="plan-start-date-input" /></Field>
            <Field label="تاريخ النهاية" required><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="plan-end-date-input" /></Field>
          </div>
          <Field label="الوصف والهدف"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} rows={3} data-testid="plan-description-input" /></Field>
          <PrimaryBtn onClick={createPlan} disabled={saving} data-testid="plan-create-submit-btn">إنشاء الخطة</PrimaryBtn>
        </div>
      </Modal>
    </div>
  );
}
